import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { IoCloseOutline, IoPencilOutline, IoSparklesOutline, IoTrashOutline, IoArchiveOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { useToast } from '../context/ToastContext';
import { useDarkMode } from '../context/DarkModeContext';
import { formatDate } from '../utils/dateUtils';
import { CareerCard, ImpactHistoryEntry } from '../types/careerCard';
import { useCareerCard } from '../context/CareerCardContext';
import { EditMode } from './EditCardPanel/EditMode';
import { SuggestionPanel } from './EditCardPanel/SuggestionPanel';
import { Suggestion } from '../types/suggestions';
import { supabase } from '../utils/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { LinkedContent } from './LinkedContentPreview';
import { useCachedData } from '../hooks/useCachedData';
import { CACHE_KEYS, CACHE_DURATIONS } from '../constants/cacheKeys';
import { ImpactMetricsSection } from './ImpactMetrics/ImpactMetricsSection';
import { ViewMode } from './ViewMode';
import { EditState } from '../types/editState';
import { UnsavedChangesDot } from './UnsavedChangesDot';
import { SaveButton } from './SaveButton';
import { useAutoSave } from '../hooks/useAutoSave';
import { AutoSaveStatus } from './AutoSaveStatus';
import { useAuthState } from '../hooks/useAuthState';
import { renderToDoActions } from './ProjectCardActions';
import { useActiveCareerFocus } from '../hooks/useCareerFocus';
import { useRubric } from '../hooks/useRubric';

const ARCHIVE_TAG = 'Archived';
const ACTIVE_TAG = 'Active';

interface CareerCardPanelProps {
  card: CareerCard;
  onClose: () => void;
  onSave: (card: CareerCard) => Promise<CareerCard>;
  onDelete: (cardId: string) => Promise<void>;
  startInEditMode?: boolean;
  skipAnimation?: boolean;
  editState: EditState<CareerCard> | null;
  setEditState: (state: EditState<CareerCard> | null) => void;
  onCardChange: (updatedCard: CareerCard) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  onSaveCard: (card: CareerCard) => Promise<void>;
  cardTypes: Array<{ id: string; name: string }>;
  onLinkWorkExperience?: (cardId: string, workExperienceId: string) => Promise<void>;
  onUnlinkWorkExperience?: (cardId: string, workExperienceId: string) => Promise<void>;
  linkedContent?: { [key: string]: any };
}

export const CareerCardPanel: React.FC<CareerCardPanelProps> = ({
  card,
  onClose,
  onSave,
  onDelete,
  startInEditMode = false,
  skipAnimation = false,
  editState,
  setEditState,
  onCardChange,
  isEditing,
  setIsEditing,
  onSaveCard,
  cardTypes,
  onLinkWorkExperience,
  onUnlinkWorkExperience,
  linkedContent,
}) => {
  const { showToast } = useToast();
  const { darkMode } = useDarkMode();
  const { user, isAuthenticated } = useAuthState();
  const { focus } = useActiveCareerFocus();
  const { activeRubric } = useRubric(focus?.id);
  const [isEditMode, setIsEditMode] = useState(startInEditMode);
  const [editedCard, setEditedCard] = useState<CareerCard>(card);
  const { schemas, isLoading: schemasLoading, forceRefresh } = useCareerCard();
  const schema = schemas.find(s => s.card_type === editedCard.card_data.type)?.schema;
  const [hasChanges, setHasChanges] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [acceptedFields, setAcceptedFields] = useState<Set<string>>(new Set());
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [previousValues, setPreviousValues] = useState<Record<string, unknown>>({});
  const queryClient = useQueryClient();
  const [linkedWorkExperience, setLinkedWorkExperience] = useState<LinkedContent | null>(null);
  const [pendingWorkExperienceId, setPendingWorkExperienceId] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const {
    data: workExperiences = [],
  } = useCachedData<LinkedContent[]>(
    async () => {
      const { data, error } = await supabase
        .from('work_experience')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;

      return data.map((exp: any) => ({
        id: exp.id,
        type: 'work_experience' as const,
        preview: {
          company: exp.company,
          position: exp.position,
          date_range: {
            start: exp.start_date,
            end: exp.end_date || undefined,
          },
        },
      }));
    },
    {
      cacheKey: CACHE_KEYS.WORK_EXPERIENCES,
      maxAge: CACHE_DURATIONS.SHORT,
      initialData: [],
    }
  );

  const {
    lastSaved,
    isSaving,
    error: autoSaveError,
  } = useAutoSave(editState, onSave, {
    enabled: isEditMode,
    interval: 30000, // 30 seconds
  });

  useEffect(() => {
    if (!editState) {
      setEditedCard(card);
      setIsEditMode(!card.cardid || startInEditMode);
      setHasChanges(!card.cardid);
      setSuggestions([]);
      setAcceptedFields(new Set());
      setPreviousValues({});
    }
  }, [card.cardid, editState, startInEditMode]);

  useEffect(() => {
    if (editState) {
      setEditedCard(editState.currentContent);
      setHasChanges(editState.hasUnsavedChanges);
      setIsEditMode(true);
    }
  }, [editState]);

  useEffect(() => {
    const fetchLinkedContent = async () => {
      if (!navigator.onLine) {
        setLinkedWorkExperience(null);
        return;
      }

      setIsLoadingSuggestions(true);
      try {
        // Fetch work experience links
        const { data: links, error } = await supabase
          .from('content_links')
          .select('*')
          .eq('source_id', card.cardid)
          .filter('source_type', 'eq', 'career_card')
          .filter('target_type', 'eq', 'work_experience');

        if (error) throw error;

        if (!links || links.length === 0) {
          setLinkedWorkExperience(null);
        } else {
          const linkedExp = workExperiences?.find(exp => exp.id === links[0].target_id);
          if (linkedExp) {
            setLinkedWorkExperience(linkedExp);
          }
        }

      } catch (error) {
        if (navigator.onLine) {
          console.error('Error fetching linked content:', error);
          showToast('Failed to fetch linked content', 'error');
        }
        setLinkedWorkExperience(null);
      } finally {
        setIsLoadingSuggestions(false);
      }
    };

    if (card.cardid) {
      fetchLinkedContent();
    }
  }, [card.cardid, workExperiences]);

  useEffect(() => {
    if (schemas.length === 0 && !schemasLoading && card.card_data?.type) {
      forceRefresh();
    }
  }, [schemas.length, schemasLoading, card.card_data?.type, forceRefresh]);

  const handleGetSuggestions = async () => {
    try {
      setIsLoadingSuggestions(true);
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/suggest-card-updates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          cardData: editedCard.card_data,
          cardSchema: schema,
          includePrompts: true,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to get suggestions: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.suggestions || !Array.isArray(data.suggestions)) {
        throw new Error('Invalid response format from suggestions API');
      }

      const validSuggestions = data.suggestions.filter((s: { type: string }) =>
        s.type === 'factual' || s.type === 'prompt'
      );

      setSuggestions(validSuggestions);
    } catch (error) {
      console.error('Error getting suggestions:', error);
      showToast('Failed to get suggestions', 'error');
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleAcceptSuggestion = (suggestion: Suggestion) => {
    if (suggestion.type === 'prompt') {
      return;
    }

    setPreviousValues(prev => ({
      ...prev,
      [suggestion.field]: editedCard.card_data[suggestion.field],
    }));

    let suggestedValue = suggestion.suggestedValue;
    if (typeof suggestedValue === 'string') {
      try {
        suggestedValue = JSON.parse(suggestedValue);
      } catch {
        suggestedValue = suggestion.suggestedValue;
      }
    }

    const updatedCard = {
      ...editedCard,
      card_data: {
        ...editedCard.card_data,
        [suggestion.field]: suggestedValue
      }
    };

    setEditedCard(updatedCard);
    setHasChanges(true);
    setAcceptedFields(prev => new Set([...prev, suggestion.field]));

    handleSave().catch(error => {
      console.error('Error saving suggestion:', error);
      showToast('Failed to save suggestion', 'error');
    });
  };

  const handleRejectSuggestion = (suggestion: Suggestion) => {
    setSuggestions(prev => prev.filter(s => s.field !== suggestion.field));
  };

  const handleUndoSuggestion = (field: string) => {
    if (field in previousValues) {
      const updatedCard = {
        ...editedCard,
        card_data: {
          ...editedCard.card_data,
          [field]: previousValues[field]
        }
      };
      setEditedCard(updatedCard);
      setHasChanges(true);

      setAcceptedFields(prev => {
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
      setPreviousValues(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleLinkWorkExperience = async (workExpId: string) => {
    if (!card.cardid) {
      setPendingWorkExperienceId(workExpId);
      const newLinkedExp = workExperiences?.find(exp => exp.id === workExpId);
      if (newLinkedExp) {
        setLinkedWorkExperience(newLinkedExp);
      }
      return;
    }

    try {
      if (linkedWorkExperience) {
        await supabase
          .from('content_links')
          .delete()
          .filter('source_id', 'eq', card.cardid)
          .filter('source_type', 'eq', 'career_card')
          .filter('target_type', 'eq', 'work_experience');
      }

      if (!isAuthenticated || !user) {
        throw new Error('No active session');
      }

      const { error } = await supabase
        .from('content_links')
        .insert({
          source_id: card.cardid,
          source_type: 'career_card',
          target_id: workExpId,
          target_type: 'work_experience',
          user_id: user.id,
        });

      if (error) throw error;

      const newLinkedExp = workExperiences?.find(exp => exp.id === workExpId);
      if (newLinkedExp) {
        setLinkedWorkExperience(newLinkedExp);
      }

      showToast('Successfully linked work experience', 'success');
    } catch (error) {
      console.error('Error linking work experience:', error);
      showToast('Failed to link work experience', 'error');
    }
  };

  const handleUnlinkWorkExperience = async () => {
    if (!card.cardid) {
      setPendingWorkExperienceId(null);
      setLinkedWorkExperience(null);
      return;
    }

    if (!linkedWorkExperience) return;

    try {
      const { error } = await supabase
        .from('content_links')
        .delete()
        .filter('source_id', 'eq', card.cardid)
        .filter('source_type', 'eq', 'career_card')
        .filter('target_type', 'eq', 'work_experience');

      if (error) throw error;

      setLinkedWorkExperience(null);
      showToast('Successfully unlinked work experience', 'success');
    } catch (error) {
      console.error('Error unlinking work experience:', error);
      showToast('Failed to unlink work experience', 'error');
    }
  };

  const handleSave = async () => {
    if (!editedCard) return;

    if (!editedCard.card_data.title?.trim()) {
      showToast('Please enter a title for the card', 'error');
      return;
    }

    try {
      if (editState) {
        setEditState({
          ...editState,
          isSaving: true,
          saveError: undefined
        });
      }

      const savedCard = await onSave(editedCard);

      // Handle all pending links after successful save
      if (savedCard.cardid) {
        const linkPromises = [];
        
        // Handle pending work experience link
        if (pendingWorkExperienceId) {
          linkPromises.push(
            (async () => {
              try {
                if (!isAuthenticated || !user) {
                  throw new Error('No active session');
                }

                const { error } = await supabase
                  .from('content_links')
                  .insert({
                    source_id: savedCard.cardid,
                    source_type: 'career_card',
                    target_id: pendingWorkExperienceId,
                    target_type: 'work_experience',
                    user_id: user.id,
                  });

                if (error) throw error;

                const linkedExp = workExperiences?.find(exp => exp.id === pendingWorkExperienceId);
                if (linkedExp) {
                  setLinkedWorkExperience(linkedExp);
                }

                setPendingWorkExperienceId(null);
                showToast('Successfully linked work experience', 'success');
              } catch (error: unknown) {
                console.error('Error creating work experience link:', error);
                showToast('Failed to link work experience', 'error');
              }
            })()
          );
        }
        
        // Execute all link promises
        if (linkPromises.length > 0) {
          await Promise.all(linkPromises);
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['careerCards'] });
      await queryClient.refetchQueries({ queryKey: ['careerCards'] });

      setEditedCard({
        ...savedCard,
        career_impact: savedCard.career_impact || editedCard.career_impact,
        engagement_level: savedCard.engagement_level || editedCard.engagement_level
      });
      setHasChanges(false);
      setSuggestions([]);
      setAcceptedFields(new Set());

      if (editState) {
        setEditState(null);
      }

      if (!isEditMode) {
        onClose();
      } else {
        setIsEditMode(false);
      }
    } catch (error: unknown) {
      console.error('Error saving card:', error);
      showToast('Failed to save card', 'error');
      if (editState) {
        setEditState({
          ...editState,
          isSaving: false,
          saveError: error instanceof Error ? error.message : 'Failed to save card'
        });
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this card?')) {
      try {
        await onDelete(card.cardid);
        showToast('Card deleted successfully', 'success');
        onClose();
      } catch (error) {
        console.error('Error deleting card:', error);
        showToast('Failed to delete card', 'error');
      }
    }
  };

  const handleArchiveToggle = async () => {
    const isArchived = editedCard.card_data.custom_tags?.includes(ARCHIVE_TAG);
    let newTags: string[];

    if (isArchived) {
      newTags = (editedCard.card_data.custom_tags || [])
        .filter(tag => tag !== ARCHIVE_TAG)
        .filter(tag => tag !== ACTIVE_TAG);
      newTags = [ACTIVE_TAG, ...newTags];
    } else {
      newTags = (editedCard.card_data.custom_tags || [])
        .filter(tag => tag !== ACTIVE_TAG)
        .filter(tag => tag !== ARCHIVE_TAG);
      newTags = [ARCHIVE_TAG, ...newTags];
    }

    const updatedCard = {
      ...editedCard,
      card_data: {
        ...editedCard.card_data,
        custom_tags: newTags
      }
    };

    try {
      await onSave(updatedCard);
      showToast(
        <div className="flex items-center justify-between w-full gap-4">
          <span>{`Card ${isArchived ? 'unarchived' : 'archived'} successfully`}</span>
          <button
            onClick={handleSave}
            className="px-2 py-1 text-xs bg-white/25 rounded hover:bg-white/40 transition-colors"
          >
            Undo
          </button>
        </div>,
        'success'
      );
      onClose();
    } catch (error) {
      console.error('Error archiving card:', error);
      showToast(`Failed to ${isArchived ? 'unarchive' : 'archive'} card`, 'error');
    }
  };

  const handleChange = (updatedCard: CareerCard) => {
    setEditedCard(updatedCard);
    setHasChanges(true);

    // When in edit mode, also update the edit state
    if (isEditMode && editState) {
      setEditState({
        ...editState,
        currentContent: updatedCard,
        hasUnsavedChanges: true
      });
    }

    if (onCardChange) {
      onCardChange(updatedCard);
    }

    // Log the change for debugging
    // if (isEditMode) {
    //   console.log('Card updated in edit mode:', {
    //     updatedCard,
    //     editState: editState ? {
    //       ...editState,
    //       currentContent: '...',
    //       originalContent: '...'
    //     } : null
    //   });
    // }
  };

  const handleEditModeToggle = () => {
    if (isEditMode) {
      // If already in edit mode, we're toggling off - handle as a cancel
      handleCancel();
    } else {
      // Create a deep copy of the original card for both original and current content
      const cardCopy = JSON.parse(JSON.stringify(card));
      
      // Set the edit state with proper original and current content copies
      setEditState({
        hasUnsavedChanges: false,
        originalContent: cardCopy,
        currentContent: cardCopy,
      });
      
      setEditedCard(cardCopy);
      setIsEditMode(true);
      
      console.log('Entering edit mode with new edit state');
    }
  };

  const handleCancel = () => {
    if (editState?.hasUnsavedChanges || hasChanges) {
      const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmCancel) return;
    }

    if (!card.cardid) {
      onClose();
    } else {
      // Create a deep copy of the original card to ensure we properly reset state
      const originalCard = JSON.parse(JSON.stringify(card));
      
      // Reset to original state
      setEditedCard(originalCard);
      setEditState(null);
      setIsEditMode(false);
      setHasChanges(false);
      
      console.log('Cancelled edit, restored original card state:', {
        original: originalCard,
        current: editedCard
      });
    }
  };

  const handleMetricChange = (type: 'career_impact' | 'engagement_level', value: number) => {
    const newEntry: ImpactHistoryEntry = {
      timestamp: new Date().toISOString(),
      career_impact: type === 'career_impact' ? value : (editedCard.career_impact?.value || 1),
      engagement_level: type === 'engagement_level' ? value : (editedCard.engagement_level?.value || 1)
    };

    const currentHistory = editedCard.card_data.impact_history?.entries || [];
    const newHistory = [...currentHistory, newEntry];

    const updatedCard = {
      ...editedCard,
      [type]: {
        value,
        last_updated: formatDate(new Date())
      },
      card_data: {
        ...editedCard.card_data,
        impact_history: {
          entries: newHistory
        }
      }
    };

    setEditedCard(updatedCard);
    handleChange(updatedCard);
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;

    const tag = newTag.trim();
    if (!editedCard.card_data.custom_tags?.includes(tag)) {
      const updatedCard = {
        ...editedCard,
        card_data: {
          ...editedCard.card_data,
          custom_tags: [...(editedCard.card_data.custom_tags || []), tag]
        }
      };
      handleChange(updatedCard);
    }
    setNewTag('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (tagToRemove === ACTIVE_TAG) return;
    const updatedCard = {
      ...editedCard,
      card_data: {
        ...editedCard.card_data,
        custom_tags: editedCard.card_data.custom_tags?.filter(tag => tag !== tagToRemove) || []
      }
    };
    handleChange(updatedCard);
  };

  const generateTags = async () => {
    if (!editedCard.card_data.title?.trim() && !editedCard.card_data.overview?.trim()) {
      showToast('Please add a title or overview before generating tags', 'error');
      return;
    }

    setIsGeneratingTags(true);
    try {
      const { data, error } = await supabase.functions.invoke('suggest-tags', {
        body: {
          content: `${editedCard.card_data.title}\n${editedCard.card_data.overview || ''}`,
          existingTags: editedCard.card_data.custom_tags || []
        }
      });

      if (error) throw error;
      if (data?.suggestedTags) {
        setSuggestedTags(data.suggestedTags);
      }
    } catch (error) {
      console.error('Error generating tags:', error);
      showToast('Failed to generate tags', 'error');
    } finally {
      setIsGeneratingTags(false);
    }
  };

  const handleAddSuggestedTag = (tag: string) => {
    if (!editedCard.card_data.custom_tags?.includes(tag)) {
      const updatedCard = {
        ...editedCard,
        card_data: {
          ...editedCard.card_data,
          custom_tags: [...(editedCard.card_data.custom_tags || []), tag]
        }
      };
      handleChange(updatedCard);
      setSuggestedTags(prev => prev.filter(t => t !== tag));
    }
  };

  const handleCompleteFollowUp = async (followUp: any, index: number) => {
    try {
      setActionInProgress(`followUp_${index}`);

      const completionDate = new Date().toISOString().split('T')[0];

      const historyEntry = {
        date: completionDate,
        interaction_type: "Follow-up",
        notes: followUp.notes || "",
        outcome: followUp.outcome || "Completed follow-up",
        ...followUp
      };

      const updatedCard = { ...editedCard };

      const relationshipHistory = Array.isArray(updatedCard.card_data.relationship_history) 
        ? updatedCard.card_data.relationship_history 
        : [];
        
      updatedCard.card_data.relationship_history = [
        ...relationshipHistory,
        historyEntry
      ];

      const activeFollowUps = Array.isArray(updatedCard.card_data.active_follow_ups) 
        ? [...updatedCard.card_data.active_follow_ups] 
        : [];
        
      activeFollowUps.splice(index, 1);
      updatedCard.card_data.active_follow_ups = activeFollowUps;

      setEditedCard(updatedCard);
      handleChange(updatedCard);
      
      showToast(
        <div className="flex items-center justify-between w-full gap-4">
          <span>Follow up moved to relationship history</span>
        </div>,
        'success'
      );
    } catch (error) {
      console.error("Error completing follow-up:", error);
      showToast('Failed to complete follow-up', 'error');
    } finally {
      setActionInProgress(null);
    }
  };

  const renderFollowUpActions = (followUp: any, index: number) => {
    if (!isEditMode) return null;
    
    return (
      <div className="follow-up-actions flex space-x-2 mt-2 justify-end">
        <button
          className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center text-sm"
          onClick={() => handleCompleteFollowUp(followUp, index)}
          disabled={actionInProgress === `followUp_${index}`}
          title="Mark as complete"
        >
          <IoCheckmarkCircleOutline className="mr-1" /> 
          Complete
        </button>
      </div>
    );
  };

  const renderFieldActions = (followUp: any, index: number) => {
    // Extract the field name from the item
    const fieldName = followUp.__fieldName;
    
    // For People cards - active_follow_ups
    if (fieldName === 'active_follow_ups' && renderFollowUpActions) {
      return renderFollowUpActions(followUp, index);
    }
    
    // For Project cards - active_to_do
    if (fieldName === 'active_to_do' && editedCard.card_data.type === 'Project') {
      return renderToDoActions(
        followUp, 
        index, 
        editedCard, 
        handleChange, 
        actionInProgress, 
        setActionInProgress, 
        showToast, 
        isEditMode
      );
    }
    
    return null;
  };

  return (
    <motion.div
      initial={skipAnimation ? false : { x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="h-full flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-800"
    >
      <div className="sticky top-0 bg-white dark:bg-gray-800 z-10">
        <div className="flex justify-between items-start p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="grow pr-5">
            {isEditMode ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="relative grow">
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={editedCard.card_data.title || ''}
                        onChange={(e) => handleChange({
                          ...editedCard,
                          card_data: {
                            ...editedCard.card_data,
                            title: e.target.value
                          }
                        })}
                        className="w-full text-xl font-bold bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500"
                        placeholder="Enter title..."
                      />
                      <UnsavedChangesDot visible={editState?.hasUnsavedChanges || false} />
                    </div>
                    <div className="absolute -bottom-6 left-0">
                      <AutoSaveStatus
                        lastSaved={lastSaved}
                        isSaving={isSaving}
                        error={autoSaveError}
                      />
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCancel}
                      className="px-3 py-1 text-sm rounded-md bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <SaveButton
                      onClick={handleSave}
                      hasChanges={editState?.hasUnsavedChanges || hasChanges}
                      disabled={!editedCard.card_data.title?.trim()}
                    />
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                      title="Close panel"
                    >
                      <IoCloseOutline size={20} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span>Created: {formatDate(new Date(editedCard.created_at))}</span>
                  {editedCard.card_data.custom_tags && editedCard.card_data.custom_tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {editedCard.card_data.custom_tags.map((tag, index) => (
                        <span
                          key={index}
                          className={`px-2 py-1 text-sm rounded-full ${tag === ARCHIVE_TAG
                              ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                              : tag === ACTIVE_TAG
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'bg-gray-200 dark:bg-gray-700'
                            }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <h2 className="text-xl font-bold">{editedCard.card_data.title}</h2>
                    <UnsavedChangesDot visible={editState?.hasUnsavedChanges || false} />
                  </div>
                  <div className="flex space-x-2">
                    {!isEditMode ? (
                      <>
                        <button
                          onClick={handleArchiveToggle}
                          className={`p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full ${editedCard.card_data.custom_tags?.includes(ARCHIVE_TAG) ? 'text-gray-600' : ''
                            }`}
                          title={editedCard.card_data.custom_tags?.includes(ARCHIVE_TAG) ? 'Unarchive card' : 'Archive card'}
                        >
                          <IoArchiveOutline size={20} />
                        </button>
                        <button
                          onClick={handleEditModeToggle}
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                          title="Edit card"
                        >
                          <IoPencilOutline size={20} />
                        </button>
                        <button
                          onClick={handleDelete}
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full text-red-500"
                          title="Delete card"
                        >
                          <IoTrashOutline size={20} />
                        </button>
                        <button
                          onClick={onClose}
                          className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full"
                          title="Close panel"
                        >
                          <IoCloseOutline size={20} />
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-col gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span>Created: {formatDate(new Date(editedCard.created_at))}</span>
                  {editedCard.card_data.custom_tags && editedCard.card_data.custom_tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {editedCard.card_data.custom_tags.map((tag, index) => (
                        <span
                          key={index}
                          className={`px-2 py-1 text-sm rounded-full ${tag === ARCHIVE_TAG
                              ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                              : tag === ACTIVE_TAG
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'bg-gray-200 dark:bg-gray-700'
                            }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {suggestions.length > 0 && !isEditMode && (
          <div className="border-t border-gray-200 dark:border-gray-700">
            <SuggestionPanel
              suggestions={suggestions}
              onAccept={handleAcceptSuggestion}
              onReject={handleRejectSuggestion}
              onUndo={handleUndoSuggestion}
              acceptedFields={acceptedFields}
              cardType={editedCard.card_data.type}
            />
          </div>
        )}

        <ImpactMetricsSection
          careerImpact={editedCard.career_impact}
          engagementLevel={editedCard.engagement_level}
          isEditMode={isEditMode}
          cardType={editedCard.card_data.type}
          onMetricChange={handleMetricChange}
          historyEntries={editedCard.card_data.impact_history?.entries || []}
        />

        {isEditMode ? (
          <div className="p-4">
            <div className="mb-4">
              <div className="space-y-2">
                <div>
                  <h4 className="text-sm font-medium mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {editedCard.card_data.custom_tags?.map((tag, index) => (
                      <span
                        key={index}
                        className={`px-2 py-1 text-sm rounded-full flex items-center ${tag === ARCHIVE_TAG
                            ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                            : tag === ACTIVE_TAG
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-gray-200 dark:bg-gray-700'
                          }`}
                      >
                        {tag}
                        {tag !== ACTIVE_TAG && tag !== ARCHIVE_TAG && (
                          <button
                            onClick={() => handleRemoveTag(tag)}
                            className="ml-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                  <div className="flex mb-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      placeholder="Add a new tag"
                      className="grow p-2 rounded-l-lg border dark:bg-gray-700 dark:border-gray-600"
                    />
                    <button
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  <button
                    onClick={generateTags}
                    disabled={isGeneratingTags}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-500"
                  >
                    {isGeneratingTags ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        Generating suggestions...
                      </>
                    ) : (
                      <>
                        <IoSparklesOutline size={16} />
                        Suggest tags
                      </>
                    )}
                  </button>
                </div>
                {suggestedTags.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-1">Suggested tags:</p>
                    <div className="flex flex-wrap gap-2">
                      {suggestedTags.map((tag, index) => (
                        <button
                          key={index}
                          onClick={() => handleAddSuggestedTag(tag)}
                          className="text-xs px-2.5 py-0.5 rounded border border-blue-300 hover:bg-blue-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                        >
                          + {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <EditMode
              card={editedCard}
              onChange={handleChange}
              selectedCardType={editedCard.card_data.type}
              schema={schema}
              linkedContent={linkedWorkExperience}
              onLinkWorkExperience={handleLinkWorkExperience}
              onUnlinkWorkExperience={handleUnlinkWorkExperience}
              renderFollowUpActions={renderFieldActions}
            />
          </div>
        ) : (
          <ViewMode
            card={editedCard}
            schema={schema}
            linkedContent={linkedWorkExperience}
            onLinkWorkExperience={handleLinkWorkExperience}
            onUnlinkWorkExperience={handleUnlinkWorkExperience}
            isSchemaLoading={schemasLoading}
          />
        )}
      </div>
    </motion.div>
  );
}; 